import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CellProps, Column } from 'react-table';
import { toast } from 'react-toastify';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import CobuTableAdvanced from '~/components/CobuTableAdvanced/CobuTableAdvanced';
import CobuSelect from '~/components/CobuSelect/CobuSelect';
import Label from '~/components/Label/index';
import Spinner from '~/components/Spinner/Spinner';
import ToggleSwitch from '~/components/ToggleSwitch/ToggleSwitch';
import { Roles } from '~/enums/Roles';
import { SelectType } from '~/enums/SelectType';
import { useBuilding } from '~/hooks/building/useBuilding';
import { RootState } from '~/redux/store';
import { SelectedOptionType } from '~/types/form';
import {
  fetchListings,
  Listing,
  ListingCategory,
  ListingStatus,
  ListingType,
  updateListing
} from '~/api/listings';
import {
  categoryLabel,
  categoryOptions,
  formatPrice,
  statusLabel,
  statusOptions,
  toOption,
  typeLabel,
  typeOptions
} from './constants';
import ReportsModal from './ReportsModal';
import classes from './Listings.module.scss';

export const Listings = () => {
  const { buildingId } = useParams<{ buildingId: string }>();
  const { data: building } = useBuilding(buildingId);
  const { userRole } = useSelector((state: RootState) => state.auth);
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<ListingStatus>();
  const [listingType, setListingType] = useState<ListingType>();
  const [category, setCategory] = useState<ListingCategory>();
  const [reportsFor, setReportsFor] = useState<Listing | null>(null);
  const [page, setPage] = useState(1);

  // The route guard is hasAdminAccess only; the staff endpoints are VCM/Superadmin.
  const canModerate = userRole === Roles.Vcm || userRole === Roles.Superadmin;

  const listingsQueryKey = [
    'listings',
    buildingId,
    status,
    listingType,
    category,
    page
  ];
  const { data, isLoading } = useQuery(
    listingsQueryKey,
    () => fetchListings(buildingId, { status, listingType, category }, page),
    // Keep the table up while a new filter loads, instead of a full-page spinner.
    { enabled: canModerate, keepPreviousData: true }
  );
  const listings = data?.listings ?? [];
  const counts = data?.counts ?? { total: 0, hidden: 0, reported: 0 };
  const totalPages = data?.totalPages ?? 0;

  // A hide or remove can shrink the set; don't strand the user past the last page.
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const changeFilter =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setPage(1);
    };

  const updateMutation = useMutation(
    ({
      listing,
      patch
    }: {
      listing: Listing;
      patch: { hidden?: boolean; status?: ListingStatus };
    }) => updateListing(listing.uuid, patch),
    {
      onSuccess: () => queryClient.invalidateQueries(['listings', buildingId]),
      onError: () => {
        toast.error('Could not update the listing');
      }
    }
  );

  const columns = useMemo<Column<Listing>[]>(
    () => [
      {
        Header: 'LISTING',
        accessor: 'title',
        Cell: (cell: CellProps<Listing>) => {
          const listing = cell.row.original;
          const thumb = listing.images[0];
          return (
            <div className={classes.listingCell}>
              {thumb ? (
                <img
                  className={`${classes.thumb} ${
                    thumb.hidden ? classes.thumbWithheld : ''
                  }`}
                  src={thumb.url}
                  alt=''
                  title={thumb.hidden ? 'Photo withheld from residents' : ''}
                />
              ) : (
                <div className={classes.thumb} />
              )}
              <div>
                <div className={classes.listingTitle}>{listing.title}</div>
                <div className={classes.muted}>{listing.seller?.name}</div>
              </div>
            </div>
          );
        }
      },
      {
        Header: 'TYPE',
        id: 'type',
        Cell: (cell: CellProps<Listing>) => (
          <span>{typeLabel(cell.row.original.listingType)}</span>
        )
      },
      {
        Header: 'CATEGORY',
        id: 'category',
        Cell: (cell: CellProps<Listing>) => (
          <span>{categoryLabel(cell.row.original.category)}</span>
        )
      },
      {
        Header: 'PRICE',
        id: 'price',
        Cell: (cell: CellProps<Listing>) => (
          <span>{formatPrice(cell.row.original.priceCents)}</span>
        )
      },
      {
        Header: 'STATUS',
        id: 'status',
        Cell: (cell: CellProps<Listing>) => (
          <span>{statusLabel(cell.row.original.status)}</span>
        )
      },
      {
        Header: 'VIEWS',
        id: 'views',
        Cell: (cell: CellProps<Listing>) => (
          <span className={classes.muted}>{cell.row.original.viewCount}</span>
        )
      },
      {
        Header: 'REPORTS',
        id: 'reports',
        Cell: (cell: CellProps<Listing>) => {
          const count = cell.row.original.reportCount;
          if (!count) return <span className={classes.muted}>—</span>;
          return (
            <button
              className={classes.linkButton}
              onClick={() => setReportsFor(cell.row.original)}
            >
              <Label
                title={`${count} report${count === 1 ? '' : 's'}`}
                style={{ backgroundColor: '#ffe1e1', color: '#a12b2b' }}
              />
            </button>
          );
        }
      },
      {
        Header: 'CREATED',
        id: 'created',
        Cell: (cell: CellProps<Listing>) => (
          <span className={classes.muted}>
            {moment(cell.row.original.createdAt).format('ll')}
          </span>
        )
      },
      {
        Header: 'VISIBLE TO RESIDENTS',
        id: 'visibility',
        Cell: (cell: CellProps<Listing>) => {
          const listing = cell.row.original;
          const visible = !listing.hidden;
          return (
            <div className={classes.actionsCell}>
              <ToggleSwitch
                checked={visible}
                disabled={updateMutation.isLoading}
                onChange={(checked) =>
                  updateMutation.mutate({
                    listing,
                    patch: { hidden: !checked }
                  })
                }
              />
              {listing.status !== 'removed' && (
                <button
                  className='btn-ghost'
                  disabled={updateMutation.isLoading}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Remove "${listing.title}"? Residents will no longer see it.`
                      )
                    ) {
                      updateMutation.mutate({
                        listing,
                        patch: { status: 'removed' }
                      });
                    }
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          );
        }
      }
    ],
    [updateMutation]
  );

  const filterSelect = <T extends string>(
    placeholder: string,
    options: { label: string; value: T }[],
    value: T | undefined,
    onChange: (value: T | undefined) => void
  ) => (
    <CobuSelect
      selectType={SelectType.Filter}
      isClearable
      placeholder={placeholder}
      defaultValue={
        value ? options.find((option) => option.value === value) ?? null : null
      }
      options={options}
      onChange={(option: SelectedOptionType | null) =>
        onChange(toOption(options, option?.value))
      }
    />
  );

  if (!canModerate) {
    return (
      <div className='content-container'>
        <Breadcrumbs />
        <p>Only VCMs and Superadmins can moderate Marketplace listings.</p>
      </div>
    );
  }

  return (
    <div className='content-container'>
      <ReportsModal listing={reportsFor} onClose={() => setReportsFor(null)} />
      {isLoading && <Spinner />}
      {!isLoading && (
        <>
          <Breadcrumbs />
          <div className={classes.header}>
            <h1 className={classes.title}>Marketplace listings</h1>
            <p className={classes.subtitle}>
              Moderate resident listings for <b>{building?.name}</b>.
            </p>
          </div>
          <div className={classes.toolbar}>
            {filterSelect(
              'All statuses',
              statusOptions,
              status,
              changeFilter(setStatus)
            )}
            {filterSelect(
              'All types',
              typeOptions,
              listingType,
              changeFilter(setListingType)
            )}
            {filterSelect(
              'All categories',
              categoryOptions,
              category,
              changeFilter(setCategory)
            )}
            <span className={classes.count}>
              {counts.total} total · {counts.total - counts.hidden} visible ·{' '}
              {counts.hidden} hidden · {counts.reported} reported
            </span>
          </div>
          <CobuTableAdvanced
            data={listings}
            columns={columns}
            noData={{ label: 'No listings yet' }}
          />
          {totalPages > 1 && (
            <div className={classes.pager}>
              <button
                className='btn-ghost'
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                PREVIOUS
              </button>
              <span className={classes.muted}>
                Page {page} of {totalPages}
              </span>
              <button
                className='btn-ghost'
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                NEXT
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Listings;
